/**
 * UIBugReporterButton — floating "Report a UI bug" button shown to every
 * authenticated user on every page. Clicking it captures the current viewport
 * via html-to-image, prompts for an optional description, and files the
 * report to /api/ui-bugs/report. Super-admins later batch-analyze via
 * VibeKonnect's Kaharian Gateway vision ladder.
 *
 * v3.3 (2026-07-01)
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { toPng } from 'html-to-image';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Bug, Loader2, X, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Grab the last N browser console lines so super-admins can correlate the
// screenshot with what actually errored. We shim console.error/warn/log on
// mount and keep a small in-memory ring buffer.
const _consoleBuffer = [];
const _MAX_CONSOLE_LINES = 40;
let _consoleShimmed = false;

function _installConsoleShim() {
  if (_consoleShimmed || typeof window === 'undefined') return;
  _consoleShimmed = true;
  ['error', 'warn', 'log'].forEach((level) => {
    const orig = console[level];
    console[level] = (...args) => {
      try {
        const line = `[${level}] ${args.map((a) => {
          if (a instanceof Error) return `${a.message}\n${a.stack}`;
          if (typeof a === 'object') { try { return JSON.stringify(a); } catch { return String(a); } }
          return String(a);
        }).join(' ')}`.slice(0, 500);
        _consoleBuffer.push(line);
        if (_consoleBuffer.length > _MAX_CONSOLE_LINES) _consoleBuffer.shift();
      } catch { /* never crash logging */ }
      orig.apply(console, args);
    };
  });
  window.addEventListener('error', (e) => {
    _consoleBuffer.push(`[uncaught] ${e.message} @ ${e.filename}:${e.lineno}`);
    if (_consoleBuffer.length > _MAX_CONSOLE_LINES) _consoleBuffer.shift();
  });
  window.addEventListener('unhandledrejection', (e) => {
    _consoleBuffer.push(`[unhandledrejection] ${e.reason && e.reason.message ? e.reason.message : String(e.reason).slice(0, 300)}`);
    if (_consoleBuffer.length > _MAX_CONSOLE_LINES) _consoleBuffer.shift();
  });
}

export function UIBugReporterButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null); // dataURL
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => { _installConsoleShim(); }, []);

  const captureAndOpen = useCallback(async () => {
    setCapturing(true);
    // Hide our own button in the screenshot so it doesn't dominate the frame.
    const originalDisplay = buttonRef.current?.style.display;
    if (buttonRef.current) buttonRef.current.style.display = 'none';
    try {
      // toPng captures the whole document. We downscale + JPEG-quality it so
      // the base64 payload stays under a few hundred KB even on big screens.
      const dataUrl = await toPng(document.body, {
        pixelRatio: 1,
        cacheBust: true,
        // Skip inline styles that might reference external fonts (which
        // sometimes fail CORS on 3rd-party CDNs) to prevent capture aborts.
        filter: (n) => !(n?.tagName === 'LINK' && n.getAttribute('rel') === 'stylesheet' && /^https?:\/\/(?!fonts\.googleapis\.com)/.test(n.getAttribute('href') || '')),
      });
      setPreview(dataUrl);
      setDescription('');
      setSubmitted(false);
      setOpen(true);
    } catch (e) {
      toast.error('Could not capture screen. Try again — some pages with cross-origin images block capture.');
      console.warn('[UIBug] capture failed:', e);
    } finally {
      if (buttonRef.current && originalDisplay !== undefined) buttonRef.current.style.display = originalDisplay;
      setCapturing(false);
    }
  }, []);

  const submit = useCallback(async () => {
    if (!preview) return;
    setSubmitting(true);
    try {
      const b64 = preview.split(',')[1];
      await axios.post(`${API}/api/ui-bugs/report`, {
        image_base64: b64,
        media_type: 'image/png',
        url: window.location.href,
        description: description.trim() || null,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
          dpr: window.devicePixelRatio || 1,
        },
        console_snippet: _consoleBuffer.slice(-20).join('\n') || null,
      });
      setSubmitted(true);
      toast.success('Bug filed — the platform team will review shortly.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit. Try again.');
    } finally {
      setSubmitting(false);
    }
  }, [preview, description]);

  // Only show for authenticated users — placed AFTER all hooks to keep hook order stable.
  if (!user) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={captureAndOpen}
        disabled={capturing}
        aria-label="Report a UI bug"
        title="Report a UI bug — captures a screenshot and files it to the platform team"
        data-testid="ui-bug-report-btn"
        className="fixed bottom-4 right-4 z-40 h-10 w-10 sm:h-11 sm:w-auto sm:px-3 rounded-full sm:rounded-md bg-zinc-900/90 hover:bg-zinc-800 text-white shadow-lg backdrop-blur border border-zinc-700 flex items-center gap-1.5 justify-center transition-all disabled:opacity-60"
      >
        {capturing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bug className="h-4 w-4" />}
        <span className="hidden sm:inline text-xs font-medium">Report UI bug</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="ui-bug-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {submitted ? (
                <><CheckCircle2 className="h-5 w-5 text-emerald-500" /> Thanks — bug filed</>
              ) : (
                <><Bug className="h-5 w-5 text-amber-500" /> Report a UI bug</>
              )}
            </DialogTitle>
          </DialogHeader>

          {submitted ? (
            <div className="space-y-3 text-sm">
              <p className="text-gray-600">
                We&apos;ve got the screenshot, the current URL, and (if any) the last few console messages. The platform team will batch-analyze reports nightly and fix what looks wrong.
              </p>
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800">
                <div><strong>Route:</strong> {window.location.pathname}</div>
                <div><strong>Reporter:</strong> {user.email} · {user.role}</div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {preview && (
                <div className="border border-gray-200 rounded-md overflow-hidden bg-gray-50">
                  <img src={preview} alt="captured screenshot" className="w-full max-h-56 object-contain" />
                  <div className="p-1.5 text-[10px] text-gray-500 border-t border-gray-200 bg-white">
                    Captured · {window.location.pathname}
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">
                  What&apos;s wrong? <span className="text-gray-400 font-normal">(optional but helpful)</span>
                </label>
                <Textarea
                  data-testid="ui-bug-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="e.g. The credits number is cut off on mobile. Or: this button doesn't respond when I click it."
                  className="text-sm"
                  autoFocus
                />
              </div>
              <div className="text-[11px] text-gray-500">
                We&apos;ll attach the current URL ({window.location.pathname}) and the last 20 browser-console lines so we can pinpoint the fix. No mouse/keyboard input is captured.
              </div>
            </div>
          )}

          <DialogFooter className="flex-row gap-2 justify-end">
            {submitted ? (
              <Button onClick={() => setOpen(false)} data-testid="ui-bug-close">Close</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting} data-testid="ui-bug-cancel">
                  <X className="h-3.5 w-3.5 mr-1" /> Cancel
                </Button>
                <Button onClick={submit} disabled={submitting || !preview} className="bg-amber-600 hover:bg-amber-500" data-testid="ui-bug-submit">
                  {submitting ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Filing…</> : <><Bug className="h-3.5 w-3.5 mr-1" /> File report</>}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
